import React, { useRef, useState } from "react";
import { ImageFrameField } from "../../../../common/dashboard/ImageFrameField";
import { EventPartner } from "../../eventMockData";
import { DeleteConfirmModal, SuccessPopup, deletedMessage } from "./EventLibrarySelect";
import { WizardSectionCard, WizardSectionHeading, wizardId } from "./wizardShared";
import { EventReorderHandle, useEventRowReorder } from "./useEventRowReorder";

type EventPartnersSectionProps = {
  partners: EventPartner[];
  onChange: (partners: EventPartner[]) => void;
  /** Called after a partner is saved, to show the design success pop-up. */
  onSaved: () => void;
};

type PartnerDraft = { id: string | null; logoUrl: string; websiteUrl: string };

const blankDraft: PartnerDraft = { id: null, logoUrl: "", websiteUrl: "" };

const gold = "#E1A731";

// Claude Design (8) "In Partnership With" section card (Create Event Screen
// .dc.html:264-318): add a partner logo (600 x 300) and optional website link;
// partners list with Edit/Delete and drag to reorder. Hidden for
// Community/Member events by the caller.
const EventPartnersSection = ({
  partners,
  onChange,
  onSaved,
}: EventPartnersSectionProps): React.ReactElement => {
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState<PartnerDraft>(blankDraft);
  const [logoError, setLogoError] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<EventPartner | null>(null);
  const [deleted, setDeleted] = useState<{ message: string; highlights: string[] } | null>(null);
  const reorderRow = useEventRowReorder(partners, onChange);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const readFile = (file: File | null | undefined) => {
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      setUploadError("This file is not a PNG or JPG image. Choose a PNG or JPG image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("This image is larger than 5 MB. Choose a smaller image.");
      return;
    }
    setUploadError(null);
    const reader = new FileReader();
    reader.onload = () => {
      setDraft((current) => ({ ...current, logoUrl: String(reader.result || "") }));
      setLogoError(false);
    };
    reader.readAsDataURL(file);
  };

  const openForm = (partner?: EventPartner) => {
    setDraft(partner ? { id: partner.id, logoUrl: partner.logoUrl, websiteUrl: partner.websiteUrl } : blankDraft);
    setLogoError(false);
    setUploadError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setDraft(blankDraft);
    setLogoError(false);
    setUploadError(null);
  };

  const savePartner = () => {
    if (!draft.logoUrl) {
      setLogoError(true);
      return;
    }
    const saved: EventPartner = {
      id: draft.id || wizardId("partner"),
      logoUrl: draft.logoUrl,
      websiteUrl: draft.websiteUrl.trim(),
    };
    onChange(
      draft.id
        ? partners.map((partner) => (partner.id === draft.id ? saved : partner))
        : [...partners, saved],
    );
    closeForm();
    onSaved();
  };


  const partnerName = (partner: EventPartner) => partner.websiteUrl || "this partner";

  // Section card, 14px heading and 13px helper per Claude Design (8) Create
  // Event Screen.dc.html:266-270.
  return (
    <WizardSectionCard className="flex flex-col gap-[16px]">
      <WizardSectionHeading
        title="In Partnership With"
        lines={[
          "If you are offering this event in partnership with another organization click Add Partner. Add the organization logo and if you want the logo linked to their website, add the url. Click on add partner for multiple organizational partnerships.",
        ]}
      />
      <button
        type="button"
        onClick={() => openForm()}
        className="flex items-center gap-[6px] self-start rounded-[8px] border-0 bg-[#E1A731] px-[16px] py-[10px] text-[13px] font-semibold text-white hover:bg-[#C8912A]"
      >
        + Add Partner
      </button>

      {formOpen ? (
        <div className="flex flex-col gap-[16px] rounded-[12px] border border-[#ECECEC] p-[24px]">
          <div className="flex max-w-[320px] flex-col gap-[16px]">
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-semibold text-[#0A0A0A]">
                Partner Logo <span className="text-[#F0202E]">*</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                aria-label="Partner logo file"
                onChange={({ target }) => {
                  readFile(target.files?.[0]);
                  target.value = "";
                }}
              />
              <div
                className="rounded-[10px] border-2 border-dashed p-[6px]"
                style={{ borderColor: logoError ? "#F21B1B" : gold }}
              >
                <ImageFrameField
                  value={draft.logoUrl}
                  onChange={(logoUrl) => setDraft((current) => ({ ...current, logoUrl }))}
                  aspectRatio={2}
                  outputWidth={600}
                  alt="Partner logo"
                  onReplace={() => fileInputRef.current?.click()}
                  // Design (15) image slot: Replace and Edit only (decision Q8).
                  controlSize="small"
                  emptyContent={
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => {
                        event.preventDefault();
                        readFile(event.dataTransfer.files?.[0]);
                      }}
                      className="flex h-[150px] w-full items-center justify-center rounded-[8px] border border-dashed border-[#9E9E9E] bg-[#F7F7F8] px-[12px] text-center text-[13px] leading-[16px] text-[#3A3A3A]"
                    >
                      Drag and drop the logo here or click to browse — 600 x 300 PX
                    </button>
                  }
                />
              </div>
              {logoError ? (
                <span role="alert" className="text-[12px] text-[#F0202E]">
                  Add a partner logo before saving.
                </span>
              ) : null}
              {uploadError ? (
                <span role="alert" className="text-[12px] text-[#F0202E]">
                  {uploadError}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-[6px]">
              <label htmlFor="event-partner-website" className="text-[14px] font-semibold text-[#0A0A0A]">
                Website
              </label>
              <input
                id="event-partner-website"
                type="text"
                value={draft.websiteUrl}
                onChange={({ target }) => setDraft((current) => ({ ...current, websiteUrl: target.value }))}
                placeholder="https://"
                className="rounded-[8px] border border-[#9E9E9E] px-[12px] py-[10px] text-[14px] text-[#0A0A0A] outline-none focus:border-cpaGold"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-[10px]">
            <button
              type="button"
              onClick={savePartner}
              className="rounded-[8px] border-0 bg-[#E1A731] px-[16px] py-[10px] text-[13px] font-semibold text-white hover:bg-[#C8912A]"
            >
              Save Partner
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="rounded-[8px] border border-[#C7C7C7] bg-white px-[16px] py-[9px] text-[13px] font-semibold text-[#0A0A0A]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {partners.length > 0 ? (
        <>
          <div className="flex flex-col gap-[4px]">
            <h4 className="text-[14px] font-semibold text-[#0A0A0A]">Partners List</h4>
            <p className="text-[13px] text-[#0A0A0A]">
              Manage partners and arrange the order they appear on the event registration page.
            </p>
          </div>
          <div className="overflow-hidden rounded-[10px] border border-[#ECECEC]">
            <div className="grid grid-cols-[24px_110px_minmax(0,1fr)_150px] gap-[12px] bg-[#F7F7F8] px-[16px] py-[12px] text-[11px] font-semibold uppercase tracking-[.05em] text-[#0A0A0A]">
              <span />
              <span>Logo</span>
              <span>Website</span>
              <span>Actions</span>
            </div>
            {partners.map((partner, index) => {
              const reorder = reorderRow(index);
              return (
              <div
                key={partner.id}
                {...reorder.rowProps}
                className="grid grid-cols-[24px_110px_minmax(0,1fr)_150px] items-center gap-[12px] border-t border-[#ECECEC] px-[16px] py-[12px] text-[13px] text-[#0A0A0A]"
              >
                <EventReorderHandle {...reorder.handleProps} />
                <div className="flex h-[44px] w-[72px] items-center justify-center overflow-hidden">
                  <img src={partner.logoUrl} alt="" className="block max-h-full max-w-full object-contain" />
                </div>
                <span className="truncate" title={partner.websiteUrl}>
                  {partner.websiteUrl}
                </span>
                <div className="flex gap-[8px]">
                  <button
                    type="button"
                    onClick={() => openForm(partner)}
                    className="rounded-[6px] border-0 bg-[#E1A731] px-[10px] py-[6px] text-[11px] font-semibold text-white"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setToDelete(partner)}
                    className="rounded-[6px] border-0 bg-[#F21B1B] px-[10px] py-[6px] text-[11px] font-semibold text-white"
                  >
                    Delete
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </>
      ) : null}

      <DeleteConfirmModal
        open={Boolean(toDelete)}
        heading="Delete Partner"
        name={toDelete ? partnerName(toDelete) : ""}
        onConfirm={() => {
          if (toDelete) {
            onChange(partners.filter((partner) => partner.id !== toDelete.id));
            if (draft.id === toDelete.id) closeForm();
            setDeleted(deletedMessage("Partner", partnerName(toDelete)));
          }
          setToDelete(null);
        }}
        onCancel={() => setToDelete(null)}
      />
      <SuccessPopup
        open={Boolean(deleted)}
        title={deleted?.message || ""}
        highlights={deleted?.highlights}
        onClose={() => setDeleted(null)}
      />
    </WizardSectionCard>
  );
};

export default EventPartnersSection;
